import React from "react";
import * as Yup from "yup";
import { FormikValues } from "formik";

import Validation from "shared/form/Validation";
import { useCopies } from "shared/hooks/useCopies";
import { InvoicePaymentFieldName } from "./enum";
import { InvoicePaymentPropTypes } from "./types";
import { useRegDetail } from "shared/context/regDetail";
import { useInvoicePayerPaymentRequest } from "./dataloader";
import { ApiInvoicePayerPaymentRequest } from "shared/types/ApiInvoicePayerPaymentRequest";

export function useInvoicePayerPaymentService({
  invoicePaymentRequestSuccessHandler,
  ...props
}: InvoicePaymentPropTypes) {
  const copies = useCopies();
  const { id: registrationId } = useRegDetail();
  const { mutateAsync: createInvoicePayerPaymentRequest } = useInvoicePayerPaymentRequest();

  const initialValues = React.useMemo(
    () => ({
      [InvoicePaymentFieldName.EMAIL]: "",
      [InvoicePaymentFieldName.CONFIRM_EMAIL]: ""
    }),
    []
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        [InvoicePaymentFieldName.EMAIL]: Validation.emailValidation({
          required: true,
          copies
        }).test(
          "existsCheck",
          copies.get("invoice.payment.already.shared.with.payer.error.message"),
          value => props.invoicePaymentConfig?.initialEmail?.toLowerCase() !== value?.toLowerCase()
        ),
        [InvoicePaymentFieldName.CONFIRM_EMAIL]: Validation.yupIsReq({
          required: true,
          copies
        }).test(InvoicePaymentFieldName.CONFIRM_EMAIL, copies.get("email.mismatch"), function (value) {
          return this.parent.email?.toLowerCase() === value?.toLowerCase();
        })
      }),
    [copies, props.invoicePaymentConfig?.initialEmail]
  );

  const isFormReadOnly = React.useMemo(
    () => Boolean(props.invoicePaymentConfig?.isReadOnly),
    [props.invoicePaymentConfig?.isReadOnly]
  );

  const invoicePayerPaymentRequestHandler = React.useCallback(
    async (values: FormikValues) => {
      const payload: ApiInvoicePayerPaymentRequest = {
        registrationParticipantId: props.registrationParticipantId,
        purchaseOrderId: props.purchaseOrderId,
        amount: props.amount,
        invoicePayer: {
          registrationId,
          workEmail: values[InvoicePaymentFieldName.CONFIRM_EMAIL]?.toLowerCase()
        }
      };
      try {
        const data = await createInvoicePayerPaymentRequest(payload);
        invoicePaymentRequestSuccessHandler?.(data);
      } catch (e) {}
    },
    [
      createInvoicePayerPaymentRequest,
      props.amount,
      registrationId,
      props.purchaseOrderId,
      props.registrationParticipantId,
      invoicePaymentRequestSuccessHandler
    ]
  );

  return {
    initialValues,
    isFormReadOnly,
    validationSchema,
    invoicePayerPaymentRequestHandler
  };
}
